import { Radio } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ItemStatusBadge } from "@/pages/tina/components/item-status-badge";
import { UrlList } from "@/pages/tina/components/url-list";
import { KeyValueGrid } from "@/pages/tina/items/components/key-value-grid";
import { PanelShell } from "@/pages/tina/items/components/panel-shell";
import {
  type DiscoverBreakdown,
  discoverBreakdownLabel,
  discoveredUrlLabel,
  itemDiscoverBreakdown,
  itemDiscoveredCount,
  itemSourceUrl,
  shortId,
  urlTitle,
} from "@/pages/tina/items/item-context";
import type { PipelineItem } from "@/types/tina";

interface DiscoveriesPanelProps {
  item: PipelineItem;
  discoveries: PipelineItem[];
  total: number;
  isLoading: boolean;
}

export function DiscoveriesPanel({
  item,
  discoveries,
  total,
  isLoading,
}: DiscoveriesPanelProps) {
  const breakdown = itemDiscoverBreakdown(item);
  const fallbackCount = itemDiscoveredCount(item) ?? total;
  const headerCount = breakdown ? breakdown.discoveredCount : fallbackCount;

  return (
    <PanelShell
      status={item.status}
      title="Discover results"
      subtitle={
        breakdown
          ? `${discoverBreakdownLabel(breakdown)} from ${item.site}`
          : `${discoveredUrlLabel(fallbackCount)} queued from ${item.site}`
      }
      headerRight={
        <Badge variant="secondary">{headerCount.toLocaleString()}</Badge>
      }
    >
      <KeyValueGrid rows={gridRows(item, breakdown, fallbackCount, discoveries, total)} />

      <Separator />

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-14 rounded-md" />
          ))}
        </div>
      ) : breakdown ? (
        <BreakdownBody breakdown={breakdown} discoveries={discoveries} total={total} />
      ) : total === 0 ? (
        <EmptyState />
      ) : (
        <FollowerList discoveries={discoveries} className="h-[430px]" />
      )}
    </PanelShell>
  );
}

function gridRows(
  item: PipelineItem,
  breakdown: DiscoverBreakdown | null,
  fallbackCount: number,
  discoveries: PipelineItem[],
  total: number,
): { label: string; value: string; mono?: boolean }[] {
  if (breakdown) {
    return [
      { label: "Discovered", value: discoveredUrlLabel(breakdown.discoveredCount) },
      { label: "New", value: breakdown.newCount.toLocaleString() },
      {
        label: "Known",
        value: breakdown.checkedKnown
          ? (breakdown.knownCount ?? 0).toLocaleString()
          : "—",
      },
      { label: "Queued", value: breakdown.queuedCount.toLocaleString() },
      { label: "Site", value: item.site },
      { label: "Type", value: item.content_type, mono: true },
    ];
  }
  return [
    { label: "Discovered", value: discoveredUrlLabel(fallbackCount) },
    {
      label: "Showing",
      value: `${discoveries.length.toLocaleString()} of ${total.toLocaleString()}`,
    },
    { label: "Site", value: item.site },
    { label: "Type", value: item.content_type, mono: true },
  ];
}

function BreakdownBody({
  breakdown,
  discoveries,
  total,
}: {
  breakdown: DiscoverBreakdown;
  discoveries: PipelineItem[];
  total: number;
}) {
  return (
    <div className="flex flex-col gap-3">
      {breakdown.capped && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Queued {breakdown.queuedCount.toLocaleString()} of{" "}
          {breakdown.newCount.toLocaleString()} new URLs (per-run cap); the rest
          will be picked up on later runs.
        </p>
      )}
      {breakdown.truncated && (
        <p className="text-xs text-muted-foreground">
          URL lists below are truncated to the first 500; counts above are exact.
        </p>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Queued events</span>
          <Badge variant="secondary">{total}</Badge>
        </div>
        {total === 0 ? (
          <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
            No new URLs were queued — every discovered URL was already known.
          </p>
        ) : (
          <FollowerList discoveries={discoveries} className="h-56" />
        )}
      </div>

      {breakdown.checkedKnown && (
        <UrlList title="Known URLs" urls={breakdown.knownUrls} tone="known" />
      )}
      <UrlList title="All discovered" urls={breakdown.allUrls} tone="all" />
    </div>
  );
}

function FollowerList({
  discoveries,
  className,
}: {
  discoveries: PipelineItem[];
  className: string;
}) {
  return (
    <ScrollArea className={`${className} rounded-md border`}>
      <ul className="divide-y">
        {discoveries.map((discovery) => (
          <DiscoveryRow key={discovery.id} item={discovery} />
        ))}
      </ul>
    </ScrollArea>
  );
}

function EmptyState() {
  return (
    <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      No URL-bearing events were created by this discover stage.
    </div>
  );
}

function DiscoveryRow({ item }: { item: PipelineItem }) {
  const sourceUrl = itemSourceUrl(item);
  const href = sourceUrl && URL.canParse(sourceUrl) ? sourceUrl : null;
  const label = sourceUrl ? urlTitle(sourceUrl) : `item ${shortId(item.id)}`;

  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <a
            onClick={() => {
              window.open(`/tina/items/show/${item.id}`, "_blank_");
            }}
            className="font-mono text-[11px] font-bold hover:underline cursor-pointer"
          >
            {shortId(item.id)}
          </a>
          <ItemStatusBadge status={item.status} />
          {item.current_stage && (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Radio className="h-3 w-3" />
              {item.current_stage}
            </span>
          )}
        </div>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="block max-w-[680px] truncate text-blue-600 hover:underline"
          >
            {label}
          </a>
        ) : (
          <span className="block max-w-[680px] truncate text-muted-foreground">
            {label}
          </span>
        )}
      </div>
    </li>
  );
}
